import { Product, Variant } from "../../types/types";
import { hasReductionHistory } from "../variant/hasReductionHistory";
import { isInStoreOnly } from "../variant/isInStoreOnly";
import { isFutureRelease } from "../variant/isFutureRelease";
import { ensureIsActiveVariant } from "../../utils/product/ensureIsActive";
import { isVariantReducedToClear } from "./isVariantReducedToClear";

export const getSizeReductionLabelInfo = (
  product: Product,
  sizeVariant: Variant,
) => {
  const variants: Variant[] = product.variants;
  if (!allVariantsReduced(product)) {
    return { showReduced: false, label: "" };
  }

  const isReductionHistory = hasReductionHistory(sizeVariant);
  const isReducedToClear = isVariantReducedToClear(sizeVariant);

  const isReduced = isReductionHistory && isReducedToClear;

  if (!(variants[0] as Variant).differentiators?.colour) {
    return { showReduced: isReduced, label: isReduced ? "Reduced" : "" };
  }

  const transformedColours = productColourTransformer(variants);

  const colour = sizeVariant.differentiators?.colour;
  const colourOption = transformedColours?.find((option) => {
    return option.colour === colour;
  });
  if (colourOption?.someSizesReduced) {
    return { showReduced: isReduced, label: isReduced ? "Reduced" : "" };
  }
  if (colourOption?.allSizesReduced) {
    return { showReduced: false, label: "" };
  }

  return { showReduced: false, label: "" };
};

const productColourTransformer = (variants: Variant[]) => {
  if (!variants || !variants[0]?.differentiators?.colour) {
    return null;
  }
  const colours = variants.reduce(
    (
      acc: {
        colour: string;
        availableToOrder: boolean;
        colourSwatch: string | null | undefined;
        canonicalURL: string;
        isReduced: boolean;
        sizes: {
          size: string;
          availableToOrder: boolean;
          isReduced: boolean;
        }[];
        isInStoreOnly: boolean;
        isFutureRelease: boolean;
        allSizesReduced: boolean | undefined;
        someSizesReduced: boolean | undefined;
      }[],
      variant,
    ) => {
      if (!variant.differentiators) {
        return acc;
      }
      const activeVariant = ensureIsActiveVariant(variant);
      if (activeVariant) {
        if (!activeVariant.differentiators || !activeVariant.availability) {
          return acc;
        }

        const {
          differentiators: { colour, size },
          availability: { availableToOrder },
          colourSwatch,
          canonicalURL,
        } = activeVariant;

        if (!colour) {
          return acc;
        }

        const optionIndex = acc.findIndex((option) => {
          return option.colour === colour;
        });

        const isReductionHistory = hasReductionHistory(variant);
        const isReducedToClear = isVariantReducedToClear(variant);

        const isReduced = isReductionHistory && isReducedToClear;

        const isVariantFutureRelease = isFutureRelease(variant);

        if (optionIndex < 0) {
          acc.push({
            colour,
            availableToOrder,
            colourSwatch,
            canonicalURL,
            isReduced,
            sizes: [],
            isInStoreOnly: isInStoreOnly(variant),
            isFutureRelease: isFutureRelease(variant),
            allSizesReduced: undefined,
            someSizesReduced: undefined,
          });
        }

        const index = optionIndex > -1 ? optionIndex : acc.length - 1;
        if (size) {
          acc[index].isFutureRelease ||= isVariantFutureRelease;
          const sizes = acc[index].sizes;
          acc[index].sizes = [...sizes, { size, availableToOrder, isReduced }];
        }
      }

      return acc;
    },
    [],
  );

  colours.forEach((colour) => {
    const { sizes } = colour;
    if (sizes.length === 0) {
      colour.allSizesReduced = false;
      colour.someSizesReduced = false;
    } else {
      const reducedSizeCount = sizes.reduce((acc, size) => {
        return size.isReduced ? acc + 1 : acc;
      }, 0);
      const availableSizeCount = sizes.reduce((acc, size) => {
        return size.availableToOrder ? acc + 1 : acc;
      }, 0);
      if (availableSizeCount > 0) {
        colour.availableToOrder = true;
      }
      if (reducedSizeCount > 0) {
        if (reducedSizeCount === sizes.length) {
          colour.allSizesReduced = true;
          colour.someSizesReduced = false;
        } else {
          colour.allSizesReduced = false;
          colour.someSizesReduced = true;
        }
      } else {
        colour.allSizesReduced = false;
        colour.someSizesReduced = false;
      }
    }
  });

  return colours;
};

const allVariantsReduced = (product: Product) =>
  product.variants?.some((variant) => !hasReductionHistory(variant));
